import { ClientFeatures, ClientRoleEnum } from '@torim-app/types';
import { AdminRouteNames } from '@app/router/route-names';

export interface IAdminPageConfig {
  name: AdminRouteNames | string;
  title: string;
  icon: string;
  category?: string;
  parent?: AdminRouteNames;
  tabs?: string[];
  onlyAdmin?: boolean;
  allowedRoles?: ClientRoleEnum[];
  relatedFeature?: ClientFeatures;
  isMenuItem: boolean;
  customAction?: 'logout' | 'contact-us';
  /** Shown only to businesses with a payment integration, an invoice integration, or both */
  requiresPaymentsAccess?: boolean;
}

export const adminPageConfig: IAdminPageConfig[] = [
  {
    name: AdminRouteNames.Home,
    title: 'דף הבית',
    icon: 'pi-home',
    category: 'ניהול',
    isMenuItem: true,
  },
  {
    name: AdminRouteNames.BusinessConfigurations,
    title: 'שירותים',
    icon: 'pi-id-card',
    category: 'ניהול',
    isMenuItem: true,
    tabs: ['שירותים וטיפולים', 'שירותים משלימים', 'שירותים נסתרים'],
  },
  {
    name: AdminRouteNames.Clients,
    title: 'לקוחות ומשובים',
    icon: 'pi-users',
    category: 'ניהול',
    isMenuItem: true,
    tabs: ['לקוחות', 'משובים'],
  },
  {
    name: AdminRouteNames.Communication,
    title: 'אוטומציות',
    icon: 'pi-bolt',
    category: 'ניהול',
    onlyAdmin: true,
    isMenuItem: true,
    tabs: ['הודעות אוטומטיות', 'מילוי יומן חכם'],
  },
  {
    name: AdminRouteNames.Campaigns,
    title: 'קמפיין הודעות',
    icon: 'pi-send',
    category: 'שיווק',
    onlyAdmin: true,
    isMenuItem: true,
  },
  {
    name: AdminRouteNames.Vouchers,
    title: 'שוברים, כרטיסיות וקופונים',
    icon: 'pi-percentage',
    category: 'שיווק',
    onlyAdmin: true,
    relatedFeature: ClientFeatures.Vouchers,
    isMenuItem: true,
    tabs: ['כרטיסיות', 'שוברים', 'קודי קופון'],
  },
  {
    name: AdminRouteNames.Shop,
    title: 'מכירת מוצרים',
    icon: 'pi-shopping-bag',
    category: 'שיווק',
    onlyAdmin: true,
    relatedFeature: ClientFeatures.Shop,
    isMenuItem: true,
    tabs: ['מוצרים', 'הזמנות', 'הגדרות'],
  },
  {
    name: AdminRouteNames.LoyaltyProgram,
    title: 'מועדון לקוחות',
    icon: 'pi-verified',
    category: 'שיווק',
    onlyAdmin: true,
    relatedFeature: ClientFeatures.LoyaltyProgram,
    isMenuItem: true,
    tabs: ['מועדון לקוחות', 'הגדרת מועדון לקוחות'],
  },
  {
    name: AdminRouteNames.Analytics,
    title: 'ניתוחי מידע',
    icon: 'pi-chart-line',
    category: 'נתונים',
    onlyAdmin: true,
    isMenuItem: true,
  },
  {
    name: AdminRouteNames.Reports,
    title: 'דוחות וביקורת מס',
    icon: 'pi-shield',
    category: 'נתונים',
    onlyAdmin: true,
    isMenuItem: false,
  },
  {
    name: AdminRouteNames.Payments,
    title: 'כספים וסליקה',
    icon: 'pi-wallet',
    category: 'נתונים',
    onlyAdmin: true,
    isMenuItem: true,
    requiresPaymentsAccess: true,
    tabs: ['מה נסלק', 'בקשות ותשלומים', 'העברות לבנק'],
  },
  {
    name: AdminRouteNames.Configurations,
    title: 'העדפות',
    icon: 'pi-cog',
    category: 'הגדרות',
    isMenuItem: true,
    tabs: ['עמוד נחיתה', 'זימון תורים', 'תשלומים', 'משתמש'],
  },
  {
    name: AdminRouteNames.Integrations,
    title: 'חיבורים',
    icon: 'pi-link',
    category: 'הגדרות',
    onlyAdmin: true,
    isMenuItem: true,
    tabs: ['כל החיבורים', 'בוט WhatsApp'],
  },
  {
    name: 'contact-us',
    title: 'צרו קשר',
    icon: 'pi-whatsapp',
    category: 'עזרה',
    isMenuItem: true,
    customAction: 'contact-us',
  },
  {
    name: 'logout',
    title: 'התנתקות',
    icon: 'pi-sign-out',
    category: 'עזרה',
    isMenuItem: true,
    customAction: 'logout',
  },
  {
    name: AdminRouteNames.ConsentForms,
    title: 'מסמכים ואישורים',
    icon: 'pi-file',
    category: 'ניהול',
    onlyAdmin: true,
    isMenuItem: true,
  },
  // Not onlyAdmin: an employee sees their own tasks (the "team" view is managers only).
  {
    name: AdminRouteNames.Tasks,
    title: 'משימות',
    icon: 'pi-check-square',
    category: 'ניהול',
    isMenuItem: true,
  },
  // Sub-pages (not in menu)
  {
    name: AdminRouteNames.ClientProfile,
    title: 'פרופיל לקוח',
    icon: 'pi-user',
    parent: AdminRouteNames.Clients,
    isMenuItem: false,
  },
  // The iOS app's manual card charge (opened from the payment sheet as a page, see ManualCardChargeForm)
  {
    name: AdminRouteNames.ManualCardCharge,
    title: 'חיוב כרטיס',
    icon: 'pi-credit-card',
    parent: AdminRouteNames.Home,
    isMenuItem: false,
  },
  // Resources hub (team / branches / equipment) — mobile bottom-nav + desktop sidebar.
  // Excluded from the mobile drawer/More page (the bottom nav already links to it).
  // Not onlyAdmin: an employee gets the same page narrowed down to their own profile,
  // which is where they set their services and working hours.
  {
    name: AdminRouteNames.Team,
    title: 'צוות',
    icon: 'pi-box',
    category: 'ניהול',
    isMenuItem: true,
  },
  {
    name: AdminRouteNames.TeamProfile,
    title: 'פרופיל עובד/ת',
    icon: 'pi-user',
    parent: AdminRouteNames.Team,
    isMenuItem: false,
  },
  {
    name: AdminRouteNames.ResourceProfile,
    title: 'פרטי משאב',
    icon: 'pi-box',
    parent: AdminRouteNames.Team,
    onlyAdmin: true,
    isMenuItem: false,
  },
  // "More" hub — mobile bottom-nav only (desktop keeps the full sidebar).
  {
    name: AdminRouteNames.More,
    title: 'עוד',
    icon: 'pi-ellipsis-h',
    isMenuItem: false,
  },
  // Configurations drill-in sub-pages — mobile hub views driven by the ?tab query
  // param (not routes). Registered here so the mobile header shows their title +
  // a back arrow to the hub. Order matches CONFIGURATIONS_TAB_PAGES / the desktop tabs.
  {
    name: 'configurations-landing',
    title: 'עמוד נחיתה',
    icon: 'pi-palette',
    parent: AdminRouteNames.Configurations,
    isMenuItem: false,
  },
  {
    name: 'configurations-booking',
    title: 'זימון תורים',
    icon: 'pi-calendar',
    parent: AdminRouteNames.Configurations,
    isMenuItem: false,
  },
  {
    name: 'configurations-payments',
    title: 'תשלומים',
    icon: 'pi-credit-card',
    parent: AdminRouteNames.Configurations,
    isMenuItem: false,
  },
  {
    name: 'configurations-user',
    title: 'משתמש',
    icon: 'pi-user',
    parent: AdminRouteNames.Configurations,
    isMenuItem: false,
  },
];

// Tab index → sub-page name for the Configurations hub (mobile drill-in)
export const CONFIGURATIONS_TAB_PAGES = [
  'configurations-landing',
  'configurations-booking',
  'configurations-payments',
  'configurations-user',
] as const;

export const adminPageConfigMap = new Map<string, IAdminPageConfig>(
  adminPageConfig.map(p => [p.name, p])
);
